import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import classNames from "classnames";
import { Util, Hooks, Request, XtLocale } from "@xt/xtbase";
import { IconBell, IconChevronRight } from "./icons";
import styles from "./index.module.less";

const { moment, getUrl } = Util;
const { useTranslation } = Hooks;
const { XtAxios } = Request;

const LETTER_NOTICE_API = "/xt-message/public/letter-notice/list";

const AUTO_SPOT_NOTIFY_LOCAL_KEY = "autoSpotNotifyLocal";
const LETTER_NOTICE_PARAMS = { pushPage: 1, pageSize: 20, pageIndex: 1 };
const HOVER_CLOSE_MS = 400;

function getStoredTime() {
  try {
    const raw = localStorage.getItem(AUTO_SPOT_NOTIFY_LOCAL_KEY);
    if (!raw) return null;
    const { time } = JSON.parse(raw);
    return typeof time === "number" ? time : null;
  } catch (e) {
    return null;
  }
}

function getNewCount(list, storedTime) {
  if (!list || !list.length) return 0;
  if (storedTime == null) return list.length;
  return list.filter((item) => item.createTime >= storedTime).length;
}

function setStoredTimeNow() {
  try {
    localStorage.setItem(AUTO_SPOT_NOTIFY_LOCAL_KEY, JSON.stringify({ value: 1, time: Date.now() }));
  } catch (e) {}
}

/**
 * 对齐 spikex-ai-web NotificationBell：
 * - hover / click 展开毛玻璃消息栏
 * - Portal 到 body，避免顶栏 backdrop-filter 裁切模糊
 * - 无未读提示文案，底部 View all
 */
export default function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [noticeList, setNoticeList] = useState([]);
  const [storedTime, setStoredTime] = useState(null);
  const [pos, setPos] = useState({ top: 0, right: 12 });
  const t = useTranslation();
  const timerRef = useRef(null);
  const btnRef = useRef(null);
  const panelRef = useRef(null);
  const closeTimerRef = useRef(null);

  const viewAllLabel = (() => {
    const v = t("header.viewAllNotices");
    return v && v !== "header.viewAllNotices" ? v : "View all";
  })();

  const emptyLabel = (() => {
    const v = t("header.noticeNoneTip");
    return v && v !== "header.noticeNoneTip" ? v : "No announcements yet";
  })();

  const fetchNoticeList = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    const lang = (XtLocale.intl && XtLocale.intl.locale) || "en";
    XtAxios.get(LETTER_NOTICE_API, {
      params: { ...LETTER_NOTICE_PARAMS, lang },
      isAutoPopError: false,
    })
      .then((list) => {
        if (Array.isArray(list)) setNoticeList(list);
        setStoredTime(getStoredTime());
      })
      .catch(() => {})
      .finally(() => {
        timerRef.current = setTimeout(fetchNoticeList, 30000);
      });
  };

  useEffect(() => {
    setMounted(true);
    setStoredTime(getStoredTime());
    fetchNoticeList();
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    };
  }, []);

  const placePanel = () => {
    const r = btnRef.current?.getBoundingClientRect();
    if (r) {
      setPos({
        top: r.bottom + 8,
        right: Math.max(12, window.innerWidth - r.right),
      });
    }
  };

  const cancelClose = () => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  };

  const scheduleClose = () => {
    cancelClose();
    closeTimerRef.current = setTimeout(() => setOpen(false), HOVER_CLOSE_MS);
  };

  const unreadNum = getNewCount(noticeList, storedTime);
  const previewList = noticeList.slice(0, 6);

  const openPanel = () => {
    placePanel();
    setOpen(true);
  };

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      const target = e.target;
      if (panelRef.current?.contains(target) || btnRef.current?.contains(target)) return;
      setOpen(false);
    };
    const onMove = () => setOpen(false);
    document.addEventListener("mousedown", onDown);
    window.addEventListener("resize", onMove);
    window.addEventListener("scroll", onMove, true);
    return () => {
      document.removeEventListener("mousedown", onDown);
      window.removeEventListener("resize", onMove);
      window.removeEventListener("scroll", onMove, true);
    };
  }, [open]);

  const markRead = () => {
    setStoredTimeNow();
    setStoredTime(Date.now());
  };

  const onClickMsg = (item) => {
    markRead();
    const { type, webClickUrl } = item;
    if (webClickUrl) {
      window.open(webClickUrl);
    } else {
      location.href = getUrl("/notification") + (type ? "?type=" + type : "");
    }
    setOpen(false);
  };

  const handleBellClick = () => {
    if (!open) placePanel();
    setOpen((v) => !v);
  };

  const panel =
    open && mounted
      ? createPortal(
          <div
            ref={panelRef}
            role="dialog"
            className={styles.bellPanelPortal}
            style={{ top: pos.top, right: pos.right }}
            onMouseEnter={cancelClose}
            onMouseLeave={scheduleClose}
          >
            <ul className={styles.bellList}>
              {previewList.length === 0 ? (
                <li className={styles.bellEmpty}>{emptyLabel}</li>
              ) : (
                previewList.map((item, index) => (
                  <li key={"letter_" + (item.id || index)}>
                    <button type="button" className={styles.bellItem} onClick={() => onClickMsg(item)}>
                      <span className={styles.bellItemTitle}>{item.title}</span>
                      <span className={styles.bellItemTime}>
                        {moment(item.createTime).format("YYYY-MM-DD HH:mm")}
                      </span>
                    </button>
                  </li>
                ))
              )}
            </ul>
            <a
              href={getUrl("/notification")}
              className={styles.bellFooter}
              onClick={() => {
                markRead();
                setOpen(false);
              }}
            >
              {viewAllLabel}
              <IconChevronRight className={styles.bellFooterIcon} />
            </a>
          </div>,
          document.body
        )
      : null;

  return (
    <>
      <div
        className={styles.bellWrap}
        onMouseEnter={() => {
          cancelClose();
          openPanel();
        }}
        onMouseLeave={scheduleClose}
      >
        <button
          ref={btnRef}
          type="button"
          aria-label="Announcements"
          aria-expanded={open}
          className={classNames(styles.utilityBtn, open && styles.utilityBtnOpen)}
          onClick={handleBellClick}
        >
          <IconBell className={styles.utilityIcon} />
          {unreadNum > 0 && <span className={styles.bellDot} aria-hidden />}
        </button>
      </div>
      {panel}
    </>
  );
}
